import { useEffect, useState } from 'react';

// MUI
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { Box, Button, Divider, IconButton, Menu } from '@mui/material';
import { styled } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';

// Services
import AuthService from '../../../services/auth.service';
import { getCompanyLogo } from '../../../services/companies.services';

// Types
import { UserProfile } from '../../../types/UserProfile';

// Components
import GlobalSearch from '../search/GlobalSearch';
import NotificationsOverview from './NotificationsOverview';

// Styles
import './HeaderBar.scss';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#ef4444',
    color: '#ef4444',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    width: 10,
    height: 10,
    minWidth: 10,
    borderRadius: '50%',
    top: 2,
    right: 2,
  },
}));

interface HeaderBarProps {
  hasPermission?: boolean; //WebApp Use permissions
  /** Optional tenant/org logo shown at the top of the account menu. Omit/undefined renders nothing. */
  logoUrl?: string;
}

const HeaderBar = ({ hasPermission, logoUrl }: HeaderBarProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const handleNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => setNotificationsAnchorEl(event.currentTarget);

  const handleNotificationsClose = () => setNotificationsAnchorEl(null);

  const handleLogout = () => {
    AuthService.logout();
    setAnchorEl(null);
  };

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    AuthService.isAuthenticated().then(setIsAuthenticated);
    AuthService.getUserProfile().then(setUserProfile);

    let objectUrl: string | null = null;
    getCompanyLogo()
      .then((blob) => {
        if (blob && blob.size > 0) {
          objectUrl = URL.createObjectURL(blob);
          setCompanyLogo(objectUrl);
        }
      })
      .catch(() => setCompanyLogo(null));

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  const initials = userProfile?.given_name ? userProfile.given_name.slice(0, 2).toUpperCase() : 'UN';

  const displayName = userProfile?.given_name || [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ') || 'User';

  return (
    <Box className='headerbar'>
      <Box className='headerbar__search'>{hasPermission && <GlobalSearch />}</Box>

      {isAuthenticated && (
        <Box className='headerbar__actions'>
          {/* Todo: Implement notifications */}
          {/* <IconButton size='small' aria-label='notifications' className='headerbar__notifications' onClick={handleNotificationsOpen}>
            <StyledBadge overlap='circular' anchorOrigin={{ vertical: 'top', horizontal: 'right' }} variant='dot'>
              <NotificationsNoneOutlinedIcon sx={{ fontSize: 22 }} />
            </StyledBadge>
          </IconButton> */}

          {userProfile?.given_name && (
            <Box component='span' className='headerbar__customer-name'>
              {userProfile.given_name}
            </Box>
          )}

          <Avatar onClick={handleMenu} className='headerbar__avatar' src={companyLogo ?? undefined}>
            {initials}
          </Avatar>

          <Menu
            id='menu-appbar'
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{ className: 'account-menu-paper' }}
          >
            <Box className='account-menu'>
              {/* Tenant/org logo slot - renders nothing until a logoUrl is supplied */}
              {logoUrl && (
                <Box className='account-menu__logo'>
                  <img src={logoUrl} alt='Logo' />
                </Box>
              )}

              <Box className='account-menu__profile'>
                <Avatar className='account-menu__avatar'>{initials}</Avatar>
                <Box className='account-menu__identity'>
                  <Box className='account-menu__name'>{displayName}</Box>
                  {userProfile?.email && <Box className='account-menu__email'>{userProfile.email}</Box>}
                </Box>
              </Box>

              <Divider className='account-menu__divider' />

              <Box className='account-menu__actions'>
                <Button
                  onClick={handleLogout}
                  startIcon={<LogoutIcon fontSize='small' />}
                  className='account-menu__action account-menu__action--logout'
                  disableRipple
                >
                  Logout
                </Button>
              </Box>
            </Box>
          </Menu>

          <NotificationsOverview anchorEl={notificationsAnchorEl} open={Boolean(notificationsAnchorEl)} onClose={handleNotificationsClose} />
        </Box>
      )}
    </Box>
  );
};

export default HeaderBar;
